/**
 * DP269-D1 — the Bid Board: one row per package, the next step on every row.
 *
 * Used twice with the same data:
 *   compact  — card D.4 on the Project Desk (counts + up to 5 rows)
 *   full     — /projects/:projectId/bid-packages (counts + every row, filterable)
 *
 * GET /api/projects/{pid}/bid-board →
 * { counts: { packages, requested, received, hired, late },
 *   rows: [ { id, trade, title, revision, basis_label, state: "draft"|"requested"|"late"|"received"|"hired",
 *             sent_to, received, late, due_date, hired_name, hired_amount, changes_open } ] }
 *
 * Tokens only: bid-board.css + site-office.css classes.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/styles/site-office.css';
import '@/styles/bid-board.css';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (v) => { if (!v) return ''; const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v); const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(v); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
const STATE = { draft: ['Draft', ''], requested: ['Requested', ''], late: ['Overdue', 'late'], received: ['Received', 'wait'], hired: ['Hired', 'ok'] };
const NEXT = { draft: 'Finish and send', requested: 'Waiting · open', late: 'Nudge or extend', received: 'Review the proposal', hired: 'Open the hire' };
export default function BidBoard({ projectId, compact = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const [dropping, setDropping] = useState(null);  // BB-1 — the draft being deleted
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/projects/${projectId}/bid-board`); setData(r.data); setError(''); }
    catch (_e) { setError('Could not load the Bid Board.'); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  const ask = () => navigate(`/projects/${projectId}/ask-for-a-price`);
  // BB-1 — a draft nobody has been sent can be dropped from the board.
  const dropDraft = async (id) => {
    try { await axios.delete(`${API}/projects/${projectId}/bid-packages/${id}`); setDropping(null); load(); }
    catch (e) { setError(e?.response?.data?.detail || 'Could not delete that draft.'); setDropping(null); }
  };
  const open = (row) => (row.state === 'draft' ? navigate(`/projects/${projectId}/ask-for-a-price?package=${row.id}`) : navigate(`/projects/${projectId}/bid-packages?open=${row.id}`));
  if (error) return <div className="kbb"><div className="kbb__empty" role="alert">{error}</div></div>;
  if (!data) return <div className="kbb"><div className="kbb__empty">Opening…</div></div>;
  const c = data.counts || {};
  const rowsAll = data.rows || [];
  const rows = rowsAll.filter((r) => filter === 'all' || (filter === 'requested' && ['requested', 'late', 'draft'].includes(r.state)) || (filter === 'received' && r.state === 'received') || (filter === 'hired' && r.state === 'hired'));
  const shown = compact ? rows.slice(0, 5) : rows;
  return (
    <div className="kbb" data-testid="bid-board">
      <div className="kbb__hd"><b>Bid Board</b> · {c.packages || 0} packages · {c.late ? <span style={{ color: 'var(--km-late)' }}>{c.late} overdue</span> : 'none overdue'}
        <div className="sp">
          {compact ? <button type="button" className="kso-btn" onClick={() => navigate(`/projects/${projectId}/bid-packages`)} data-testid="bid-board-open">Open Bid Packages</button> : null}
          <button type="button" className="kso-btn brand" onClick={ask} data-testid="bid-board-ask">Ask for a price</button>
        </div>
      </div>
      <div className="kbb__counts">
        {[['all', 'Packages', c.packages || 0], ['requested', 'Requested', c.requested || 0], ['received', 'Received', c.received || 0], ['hired', 'Hired', c.hired || 0]].map(([k, l, n]) => (
          <div key={k} className={`kbb__count${k === 'requested' && c.late ? ' late' : ''}`} role="button" tabIndex={0} aria-pressed={filter === k} onClick={() => setFilter(k)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilter(k); } }} data-testid={`bid-board-count-${k}`}>
            <span className="n">{n}</span><span className="k">{l}{k === 'requested' && c.late ? ` · ${c.late} late` : ''}</span>
          </div>
        ))}
      </div>
      {shown.map((r) => {
        const [sl, sc] = STATE[r.state] || STATE.requested;
        return (
          <div className="kbb__row" key={r.id} onClick={() => open(r)} data-testid={`bid-board-row-${r.id}`}>
            <div className="t"><b>{r.trade} — {r.title}</b><span className="s">rev {r.revision}{r.basis_label ? ` · priced on ${r.basis_label}` : ''}</span></div>
            <div className="who">{r.state === 'hired'
              ? `${r.hired_name} · ${money(r.hired_amount)}${r.changes_open ? ` · ${r.changes_open} change open` : ''}`
              : r.state === 'draft' ? 'not sent yet' : `sent to ${r.sent_to} · received ${r.received}${r.late ? ` · ${r.late} late` : ''}`}</div>
            <div className={`due${r.state === 'late' ? ' late' : ''}`}>{r.state === 'hired' ? `accepted ${fmtDate(r.hired_at)}` : r.due_date ? `due ${fmtDate(r.due_date)}` : ''}</div>
            <div className="kbb__next"><span className={`kso-tag ${sc}`}>{sl}</span>
              {r.state === 'draft' ? (<>
                <button type="button" className="kso-btn brand" onClick={(e) => { e.stopPropagation(); open(r); }} data-testid={`bid-draft-continue-${r.id}`}>Continue</button>
                <button type="button" className="kso-btn" onClick={(e) => { e.stopPropagation(); setDropping(r.id); }} data-testid={`bid-draft-delete-${r.id}`}>Delete</button>
              </>) : <button type="button" className={`kso-btn${r.state === 'received' ? ' brand' : ''}`} onClick={(e) => { e.stopPropagation(); open(r); }}>{NEXT[r.state] || 'Open'}</button>}
            </div>
            {dropping === r.id ? (
              <div className="kbb__drop" onClick={(e) => e.stopPropagation()} data-testid={`bid-draft-confirm-${r.id}`}>
                <span className="t">Delete this unsent request? Nothing has left the building, and this cannot be undone.</span>
                <button type="button" className="kso-btn brand" onClick={() => dropDraft(r.id)} data-testid={`bid-draft-delete-yes-${r.id}`}>Delete the draft</button>
                <button type="button" className="kso-btn" onClick={() => setDropping(null)} data-testid={`bid-draft-delete-no-${r.id}`}>Keep it</button>
              </div>
            ) : null}
          </div>
        );
      })}
      {shown.length === 0 ? <div className="kbb__empty">{rowsAll.length ? 'Nothing in this view.' : 'No price requests yet. Press Ask for a price to start one.'}</div> : null}
      {compact && rows.length > 5 ? <div className="kbb__empty">{rows.length - 5} more on the Bid Packages page.</div> : null}
      {!compact ? <div className="kbb__empty">Prices on this board are the contractor's numbers. Markup is applied in the Estimate, not here.</div> : null}
    </div>
  );
}
